import { ResoResourceBase } from './types';

export class ResourceCache<T extends ResoResourceBase> {
  private cache: Map<string, T | T[] | null> = new Map();
  private pendingQueries: Map<string, Promise<T | T[] | null>> = new Map();
  private expirations: Map<string, number> = new Map();
  private defaultTTL: number;

  constructor(defaultTTL: number = 5 * 60 * 1000) {
    // 5 minutes default TTL
    this.defaultTTL = defaultTTL;
  }

  /**
   * Generate a cache key from a predicate object or function
   */
  getCacheKey(predicate: any): string {
    if (typeof predicate === 'function') {
      // For function predicates, use the function string as part of the key
      return `function:${predicate.toString()}`;
    }

    // For objects, JSON stringify
    return JSON.stringify(predicate);
  }

  /**
   * Check if a value exists in the cache and is not expired
   */
  has(predicate: any): boolean {
    const key = this.getCacheKey(predicate);
    if (!this.cache.has(key)) return false;

    // Check if expired
    const expiration = this.expirations.get(key);
    if (expiration && Date.now() > expiration) {
      this.invalidate(predicate);
      return false;
    }

    return true;
  }

  /**
   * Get a value from the cache
   */
  get<R extends T | T[] | null>(predicate: any): R | undefined {
    if (!this.has(predicate)) return undefined;
    const key = this.getCacheKey(predicate);
    return this.cache.get(key) as R;
  }

  /**
   * Store a value in the cache with optional TTL override
   */
  set<R extends T | T[] | null>(predicate: any, value: R, ttl?: number): void {
    const key = this.getCacheKey(predicate);
    this.cache.set(key, value);

    // Set expiration
    const expirationTime = Date.now() + (ttl || this.defaultTTL);
    this.expirations.set(key, expirationTime);
  }

  /**
   * Remove a value from the cache, or clear the entire cache if no predicate is provided
   */
  invalidate(predicate?: any): void {
    if (predicate) {
      const key = this.getCacheKey(predicate);
      this.cache.delete(key);
      this.expirations.delete(key);
      this.pendingQueries.delete(key);
    } else {
      // Invalidate everything
      this.cache.clear();
      this.expirations.clear();
      this.pendingQueries.clear();
    }
  }

  /**
   * Check if there's a pending query for a predicate
   */
  hasPendingQuery(predicate: any): boolean {
    const key = this.getCacheKey(predicate);
    return this.pendingQueries.has(key);
  }

  /**
   * Get a pending query for a predicate
   */
  getPendingQuery<R extends T | T[] | null>(
    predicate: any,
  ): Promise<R> | undefined {
    if (!this.hasPendingQuery(predicate)) return undefined;
    const key = this.getCacheKey(predicate);
    return this.pendingQueries.get(key) as Promise<R>;
  }

  /**
   * Store a pending query for a predicate
   */
  setPendingQuery<R extends T | T[] | null>(
    predicate: any,
    promise: Promise<R>,
  ): void {
    const key = this.getCacheKey(predicate);
    this.pendingQueries.set(key, promise);

    // Clean up after the promise resolves or rejects
    promise.finally(() => {
      if (this.pendingQueries.get(key) === promise) {
        this.pendingQueries.delete(key);
      }
    });
  }
}
